import { useRef, useEffect } from "react";
import { useThree, useFrame } from "@react-three/fiber";
import { useXR } from "@react-three/xr";
import { ROOM_BOUNDS } from "../world";
import * as THREE from "three";

// Cross-device locomotion: WASD / joystick to move, drag to look.
export default function Player({ input }) {
  const { camera, gl } = useThree();
  const xrSession = useXR((s) => s.session);
  const yaw = useRef(input.current.spawnYaw ?? 0);
  const pitch = useRef(0);
  const down = useRef(null);

  useEffect(() => {
    camera.rotation.order = "YXZ";
    const keys = input.current.keys;
    const kd = (e) => { keys[e.code] = true; };
    const ku = (e) => { keys[e.code] = false; };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);

    const el = gl.domElement;
    const pd = (e) => { down.current = { x: e.clientX, y: e.clientY, moved: 0 }; input.current.dragging = false; };
    const pm = (e) => {
      if (!down.current) return;
      const dx = e.movementX || 0, dy = e.movementY || 0;
      down.current.moved += Math.abs(dx) + Math.abs(dy);
      if (down.current.moved > 6) input.current.dragging = true;
      yaw.current -= dx * 0.0035;
      pitch.current = Math.max(-0.9, Math.min(0.9, pitch.current - dy * 0.0035));
    };
    const pu = () => { down.current = null; };
    el.addEventListener("pointerdown", pd);
    el.addEventListener("pointermove", pm);
    window.addEventListener("pointerup", pu);
    return () => {
      window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku);
      el.removeEventListener("pointerdown", pd); el.removeEventListener("pointermove", pm);
      window.removeEventListener("pointerup", pu);
    };
  }, [camera, gl, input]);

  useFrame((_, d) => {
    // teleport / room spawn
    const tp = input.current.teleport;
    if (tp) {
      camera.position.set(tp.pos[0], tp.pos[1], tp.pos[2]);
      yaw.current = input.current.spawnYaw ?? 0;
      pitch.current = 0;
      input.current.teleport = null;
    }
    if (xrSession) return; // headset drives the camera in VR

    camera.rotation.set(pitch.current, yaw.current, 0);

    const keys = input.current.keys;
    const [jx, jy] = input.current.move;
    let fwd = (keys.KeyW || keys.ArrowUp ? 1 : 0) - (keys.KeyS || keys.ArrowDown ? 1 : 0) + (-jy);
    let str = (keys.KeyD || keys.ArrowRight ? 1 : 0) - (keys.KeyA || keys.ArrowLeft ? 1 : 0) + (jx);
    const speed = 6 * d;
    if (fwd || str) {
      const dir = new THREE.Vector3(str, 0, -fwd).normalize();
      dir.applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw.current);
      camera.position.x += dir.x * speed;
      camera.position.z += dir.z * speed;
      camera.position.x = Math.max(-ROOM_BOUNDS, Math.min(ROOM_BOUNDS, camera.position.x));
      camera.position.z = Math.max(-ROOM_BOUNDS, Math.min(ROOM_BOUNDS, camera.position.z));
      camera.position.y = 1.6;
    }
  });

  return null;
}
